import { InjectableClass } from '@fino/lib-injection';
import angular from 'angular';

export class FormControlService extends InjectableClass {
  constructor() {
    'ngInject';
    super({});

    // BBAN data from ISO 13616, Country codes from ISO 3166 (www.iso.org).
    // Complete list see http://toms-cafe.de/iban/iban.js
    this.ibanData = [
      new Country('Germany',        'DE', '0  8n 0 ', '0  10n  0 ')
    ];

    this.capitals = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  }

  setValidity(control, key, validity, form = null) {
    const name = control.$name;
    let target, formControl;

    // Get related form target element
    try {
      // eslint-disable-next-line angular/document-service
      target = document.getElementsByClassName(`form-control-name-${name}`);
      formControl = angular.element(target[0]).parent();
    } catch (ex) { /* nothing, validation fails silently */ }

    if (!target || !formControl) {
      return;
    }

    if (!validity) {
      // Show form error
      formControl.addClass('has-error');
      control.$setValidity(key, false);
    } else {
      // Remove form error
      formControl.removeClass('has-error');
      control.$setValidity(key, true);
    }

    // Invoke validation immediately when form is given
    if (form) {
      form.$setSubmitted();
    }

    // Return the form control element
    return formControl;
  }

  /**
   * Search the country code in the iban_data list.
   */
  countryData(code) {
    const { ibanData } = this;
    for (let i = 0; i < ibanData.length; ++i) {
      if (ibanData[i].code == code) {
        return ibanData[i];
      }
    }
    return null;
  }

  splitIban(iban) {
    if (!iban) {
      return { /* empty cause no iban means no data to split */ };
    }

    // Strip all whitespaces
    iban = (iban + '').replace(/\s+/g, '');

    // NOTE: That's not the best implementation for doing this
    // but for german IBANs it's good enough
    let checksum = iban.substring(0, 4);
    let bankcode = iban.substring(4, 12);
    let accountnumber = iban.substring(12);
    return { checksum, bankcode, accountnumber };
  }

  /**
   * Calculate the checksum and assemble the IBAN.
   */
  calcIban(country, bank, account) {
    if (typeof country === 'string') {
      country = this.countryData(country);
    }
    const acc = this.fillAccount(country, account);
    const checksum = this.checksumIBAN(country.code + '00' + bank + acc);
    return country.code + checksum + bank + acc;
  }

  /**
   * Calculate 2-digit checksum of an IBAN.
   */
  checksumIBAN(iban) {
    let code     = iban.substring(0, 2);
    let checksum = iban.substring(2, 4);
    let bban     = iban.substring(4);

    // Assemble digit string
    let digits = '';
    for (let i = 0; i < bban.length; ++i) {
      let ch = bban.charAt(i).toUpperCase();
      if ('0' <= ch && ch <= '9') {
        digits += ch;
      }
      else {
        digits += this.capital2digits(ch);
      }
    }
    for (let i = 0; i < code.length; ++i) {
      let ch = code.charAt(i);
      digits += this.capital2digits(ch);
    }
    digits += checksum;

    // Calculate checksum
    checksum = 98 - this.mod97(digits);
    return this.fillZero('' + checksum, 2);
  }

  // Modulo 97 for huge numbers given as digit strings.
  mod97(digitString) {
    let m = 0;
    for (let i = 0; i < digitString.length; ++i) {
      m = (m * 10 + parseInt(digitString.charAt(i), 10)) % 97;
    }
    return m;
  }

  /**
   * Convert a capital letter into digits: A -> 10 ... Z -> 35 (ISO 13616).
   */
  capital2digits(ch) {
    let i;
    for (i = 0; i < this.capitals.length; ++i) {
      if (ch == this.capitals.charAt(i)) {
        break;
      }
    }
    return i + 10;
  }

  /**
   * Fill the account number part of IBAN with leading zeros.
   */
  fillAccount(country, account) {
    return this.fillZero(account, country.acc_lng);
  }

  /**
   * Fill the string with leading zeros until length is reached.
   */
  fillZero(s, l) {
    while (s.length < l) {
      s = '0' + s;
    }
    return s;
  }
}

/**
 * JavaScript Object for country specific iban data.
 */
class Country {
  // Constructor for Country objects.
  //
  // Arguments:
  //   name      - Name of the country
  //   code      - Country Code from ISO 3166
  //   bank_form - Format of bank/branch code part (e.g. "0 4a 0 ")
  //   acc_form  - Format of account number part (e.g. "0  11  2n")
  constructor(name, code, bank_form, acc_form) {
    this.name      = name;
    this.code      = code;
    this.bank      = this.decodeFormat(bank_form);
    this.acc       = this.decodeFormat(acc_form);
    this.bank_lng  = this.calcLength(this.bank);
    this.acc_lng   = this.calcLength(this.acc);
    this.total_lng = 4 + this.bank_lng + this.acc_lng;
  }

  decodeFormat(form) {
    const formList = [];
    const parts = form.split(' ');
    for (let i = 0; i < parts.length; ++i) {
      let part = parts[i];
      if (part != '') {
        let typ = part.charAt(part.length - 1);
        if (typ == 'a' || typ == 'n') {
          part = part.substring(0, part.length - 1);
        }
        else {
          typ = 'c';
        }
        let lng = parseInt(part);
        formList[formList.length] = [lng, typ];
      }
    }
    return formList;
  }

  calcLength(formList) {
    let sum = 0;
    for (let i = 0; i < formList.length; ++i) {
      sum += formList[i][0];
    }
    return sum;
  }
}
